import { faFilter, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import {
  ButtonGroup,
  Col,
  Container,
  Row,
  ToggleButton,
} from "react-bootstrap";
import {
  Comparator,
  dateFilter,
  selectFilter,
  textFilter,
} from "react-bootstrap-table2-filter";
import Button from "react-bootstrap/Button";
import { withTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import DeleteDialog from "../../components/dialogs/deleteDialog/DeleteDialog";
import UnsavedChangesDialog from "../../components/dialogs/unsavedChangesDialog/UnsavedChangesDialog";
import PaginationTable from "../../components/paginationTable/PaginationTable";
import APIErrorHelper from "../../helpers/APIErrorHelper";
import CamplaCoreService from "../../services/CamplaCoreService";
import StorageService from "../../services/StorageService";
import CreateScriptDialog from "./dialogs/createScriptDialog/CreateScriptDialog";
import ScriptsActionCell from "./scriptsActionCell/ScriptsActionCell";
import DateTimeHelper from "../../helpers/DateTimeHelper";
import PaginationTableFilterToggle from "../../components/paginationTableFilterToggle/PaginationTableFilterToggle";

class Scripts extends React.Component {
  tableId = "scriptsTable";

  constructor(props) {
    super(props);
    let showFilters = StorageService.tableConfigShowFilters(this.tableId);
    this.state = {
      scripts: [],
      showCreateModal: false,
      showDeleteModal: false,
      showUnsavedChangesDialog: false,
      closeAction: () => {},
      scriptToDeleteOrEdit: null,
      errorMessage: "",
      displayFilter: showFilters ? "initial" : "none",
      showFilters,
      dialogApiError: "",
      validationResult: [],
      apiError: "",
    };

    this.loadData = this.loadData.bind(this);
    this.loadCachedData = this.loadCachedData.bind(this);
    this.handleCreateScript = this.handleCreateScript.bind(this);
    this.handleDeleteScript = this.handleDeleteScript.bind(this);
    this.handleClone = this.handleClone.bind(this);
    this.hideCreationDialog = this.hideCreationDialog.bind(this);
    this.onHideCreationDialog = this.onHideCreationDialog.bind(this);
  }

  async componentDidMount() {
    this.paginationTableRef = React.createRef();
    this.loadCachedData();
    this.setState(await this.loadData());
  }

  async handleClone(script){
    const cloned = Object.assign({}, script);
    cloned["name"] = `${script["name"]}-${Date.now()}`;
    await this.handleCreateScript(cloned, true);
  }

  async handleCreateScript(scriptToCreate, isClone = false) {
    try {
      if (!isClone) scriptToCreate.script = btoa(scriptToCreate.script);
      const createdScript = await CamplaCoreService.createScript(
        scriptToCreate
      );
      const updatedScripts = [...this.state.scripts, createdScript];
      StorageService.set("scripts", updatedScripts);
      this.setState({
        showCreateModal: false,
        scripts: updatedScripts,
      });
    } catch (err) {
      try {
        const result = await CamplaCoreService.validateScript(scriptToCreate);
        if (result.valid) {
          this.showApiError(err);
        } else {
          this.setState({
            validationResult:
              JSON.parse(result.report)?.ErrorMessage ||
              this.props.t("Scripts.DefaultValidationMessage"),
          });
        }
      } catch (err) {
        this.showApiError(err);
      }
    }
  }

  showApiError(err) {
    this.setState({
      dialogApiError: APIErrorHelper.getError(
        err,
        () => this.setState({ dialogApiError: "", validationResult: [] }),
        {
          PRECONDITION_FAILED: "Scripts.APIError.PreconditionFailed",
          CONFLICT: "Scripts.APIError.CreateConflict",
        },
        this.props.t
      ),
    });
  }

  async handleDeleteScript() {
    try {
      await CamplaCoreService.deleteScript(this.state.scriptToDeleteOrEdit._id);
      const updatedScripts = this.state.scripts.filter(
        (x) => x._id !== this.state.scriptToDeleteOrEdit._id
      );
      StorageService.set("scripts", updatedScripts);
      this.setState({
        showDeleteModal: false,
        scripts: updatedScripts,
      });
    } catch (err) {
      this.setState({
        dialogApiError: APIErrorHelper.getError(
          err,
          () => this.setState({ dialogApiError: "" }),
          {
            PRECONDITION_FAILED: "Scripts.APIError.PreconditionFailed",
            NOT_FOUND: "Scripts.APIError.NotFound",
            CONFLICT: "Scripts.APIError.DeleteConflict",
          },
          this.props.t
        ),
      });
    }
  }

  loadCachedData() {
    const scripts = StorageService.getReferenceType("scripts");
    if (scripts) {
      this.setState({
        scripts,
      });
    }
  }

  async loadData() {
    try {
      const scripts = await CamplaCoreService.getAllScripts();
      StorageService.set("scripts", scripts);
      return {
        scripts,
      };
    } catch (err) {
      this.setState({
        apiError: APIErrorHelper.getError(
          err,
          () => this.setState({ apiError: "" }),
          {},
          this.props.t
        ),
      });
    }
    return {
      scripts: [],
    };
  }

  hideCreationDialog() {
    this.setState({ showCreateModal: false, dialogApiError: "" });
  }

  onHideCreationDialog(isFormDirty) {
    if (isFormDirty) {
      this.setState({
        showUnsavedChangesDialog: true,
        closeAction: this.hideCreationDialog,
      });
    } else {
      this.hideCreationDialog();
    }
  }

  render() {
    const { t } = this.props;
    const columns = [
      {
        dataField: "name",
        text: t("Scripts.ColumnName"),
        sort: true,
        filter: textFilter({
          style: { display: this.state.displayFilter },
          placeholder: t("Scripts.FilterNamePlaceholder"),
        }),
        formatter: (name, script) => (
          <Link
            className="item-link"
            to={{
              pathname: `/scripts/${script._id}`,
              state: {
                script,
              },
            }}
          >
            {name}
          </Link>
        ),
      },
      {
        dataField: "type",
        text: t("Scripts.ColumnType"),
        formatter: this.getScriptTypeValue,
        sort: true,
        filter: selectFilter({
          options: [
            { label: "Powershell Script", value: 0 },
            { label: "Shell (Bash) Script", value: 1 },
          ],
          style: { display: this.state.displayFilter },
          placeholder: t("Scripts.FilterTypePlaceholder"),
        }),
      },
      {
        dataField: "updated_at",
        text: t("Scripts.ColumnUpdatedAt"),
        sort: true,
        formatter: (cell) => DateTimeHelper.formatDate(cell),
        filter: dateFilter({
          style: { display: this.state.displayFilter },
          defaultValue: { date: undefined, comparator: Comparator.EQ },
        }),
      },
      {
        dataField: "actions",
        text: "",
        headerFormatter: () => (
            <PaginationTableFilterToggle
                onFilterToggle={(e) => {
                  this.setState({
                    showFilters: e.currentTarget.checked,
                    displayFilter: e.currentTarget.checked ? "initial" : "none",
                  });
                }}
                showFilters={this.state.showFilters}
                tableId={this.tableId} />
        ),
        headerClasses: "actionHeader",
        isDummyField: true,
        csvExport: false,
        formatter: (cell, script) => {
          return (
            <ScriptsActionCell
                onClone={() => this.handleClone(script)}
                onDelete={() =>
                this.setState({
                  showDeleteModal: true,
                  scriptToDeleteOrEdit: Object.assign({}, script),
                })
              }
            />
          );
        },
      },
    ];

    return (
      <div>
        <UnsavedChangesDialog
          show={this.state.showUnsavedChangesDialog}
          onHide={() => this.setState({ showUnsavedChangesDialog: false })}
          onSubmit={() => {
            this.setState({ showUnsavedChangesDialog: false });
            this.state.closeAction();
          }}
        />
        <DeleteDialog
          show={this.state.showDeleteModal}
          onHide={() =>
            this.setState({ showDeleteModal: false, dialogApiError: "" })
          }
          onSubmit={this.handleDeleteScript}
          dialogApiError={this.state.dialogApiError}
          title={t("Scripts.DeleteModal.Title")}
          body={t("Scripts.DeleteModal.Text", {
            instanceTemplateName: this.state.scriptToDeleteOrEdit?.name,
          })}
          rightButtonText={t("Scripts.DeleteModal.Confirm")}
          leftButtonText={t("DialogCancel")}
        />
        <CreateScriptDialog
          show={this.state.showCreateModal}
          onHide={this.onHideCreationDialog}
          onSubmit={this.handleCreateScript}
          dialogApiError={this.state.dialogApiError}
          validationResult={this.state.validationResult}
        />

        <Container className="mt-3">
          <Row className="pageTitle">
            <Col>
              <h2>{t("Scripts.Title")}</h2>
            </Col>
            <Col>
              <Button
                className="btn-green"
                style={{ float: "right" }}
                onClick={() => this.setState({ showCreateModal: true })}
              >
                <FontAwesomeIcon icon={faPlus} />
              </Button>
            </Col>
          </Row>
          {this.state.apiError ? (
            <Row>
              <Col>{this.state.apiError}</Col>
            </Row>
          ) : (
            ""
          )}
          <Row>
            <Col>
              <PaginationTable
                data={this.state.scripts}
                sizePerPage={10}
                paginationSize={3}
                columns={columns}
                keyField="_id"
                ref={this.paginationTableRef}
                id={this.tableId}
                view="scripts"
              />
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  getScriptTypeValue(type) {
    if (type) {
      return "Shell (Bash) Script";
    }
    return "Powershell Script";
  }
}

export default withTranslation()(Scripts);
